import React, { useState, useEffect } from 'react';
import {
  Box, VStack, Button, Text, Heading, Card, CardBody,
  useColorModeValue, Icon, Badge, Alert, AlertIcon,
  Spinner, useToast, Flex, Tabs, TabList, TabPanels, Tab, TabPanel
} from '@chakra-ui/react';
import { FaMicrophone, FaStop, FaVolumeUp, FaPlay, FaRobot, FaTools, FaCog, FaWaveSquare } from 'react-icons/fa';

// Components
import ConfigurationPanel from './components/ConfigurationPanel';
import FunctionCallingPanel from './components/FunctionCallingPanel';
import AdvancedVoiceSettings from './components/AdvancedVoiceSettings';
import FunctionCallMonitor from './components/FunctionCallMonitor';
import FunctionBuilder from './components/FunctionBuilder';
import VoiceActivitySettings from './components/VoiceActivitySettings'; // New component

// Hooks and Utils
import { useVapi } from './hooks/useVapi';
import { defaultFunctions } from './config/functionDefinitions';

const VoiceAIDemo = () => {
  // State
  const [useWebsiteContext, setUseWebsiteContext] = useState(true);
  const [websiteContextData, setWebsiteContextData] = useState('');
  const [availableFunctions, setAvailableFunctions] = useState(defaultFunctions);
  const [vapiConfig, setVapiConfig] = useState({
    publicKey: '', 
    assistantId: '', 
    model: 'gpt-4',
    voice: 'jennifer', 
    voiceProvider: 'playht',
    systemPrompt: 'You are a helpful AI assistant. Keep responses concise and conversational for voice interaction.',
    // Advanced Vapi Configuration
    silenceTimeoutMs: 1000,
    responseDelayMs: 400,
    llmRequestDelayMs: 200,
    interruptionsEnabled: true,
    backchannelingEnabled: true,
    backgroundDenoisingEnabled: true,
    endCallOnSilence: false,
    maxCallDurationMs: 1800000, // 30 minutes
    // Voice Activity Detection
    voiceActivityDetection: {
      enabled: true,
      threshold: 0.5,
      speechTimeoutMs: 3000,
      endpointingTimeoutMs: 1000,
      minSpeechDurationMs: 100,
      maxSpeechDurationMs: 30000,
      preSpeechPaddingMs: 300,
      postSpeechPaddingMs: 200
    },
    // Recording & Analytics
    recordingEnabled: true,
    analyticsEnabled: true,
    transcriptEnabled: true,
    summaryEnabled: true,
    // Language & Locale
    language: 'en',
    locale: 'en-US',
    // Cost Control
    maxTokens: 500,
    temperature: 0.7,
    // Server Configuration
    serverUrl: '',
    serverUrlSecret: '',
    // Advanced Features
    hipaaEnabled: false,
    clientMessages: [],
    serverMessages: [],
    transportConfigurations: []
  });

  const [voiceSettings, setVoiceSettings] = useState({
    speed: 1.0, stability: 0.5, similarityBoost: 0.5,
    style: 0.0, useSpeakerBoost: true
  });

  // Advanced Voice Activity State
  const [voiceActivityMetrics, setVoiceActivityMetrics] = useState({
    currentLevel: 0,
    isSpeaking: false,
    speechDuration: 0,
    silenceDuration: 0,
    interruptions: 0,
    lastActivity: null
  });

  // Hooks
  const {
    isConnected, isLoading, transcript, aiResponse, conversationHistory,
    lastError, functionCalls, initializeVapi, startCall, stopCall,
    // Extended hook properties for voice activity
    voiceActivity, callMetrics, callDuration, callCost
  } = useVapi();
  
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const toast = useToast();

  // Effects
  useEffect(() => {
    const savedSettings = localStorage.getItem('settings');
    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings);
        if (parsedSettings.contextText) {
          setWebsiteContextData(parsedSettings.contextText);
        }
      } catch (error) {
        console.error('Error loading website context:', error);
      }
    }

    // Load saved functions
    const savedFunctions = localStorage.getItem('customFunctions');
    if (savedFunctions) {
      try {
        const parsedFunctions = JSON.parse(savedFunctions);
        setAvailableFunctions(prev => [...prev, ...parsedFunctions]);
      } catch (error) {
        console.error('Error loading custom functions:', error);
      }
    }

    // Load saved Vapi configuration
    const savedVapiConfig = localStorage.getItem('vapiAdvancedConfig');
    if (savedVapiConfig) {
      try {
        const parsedConfig = JSON.parse(savedVapiConfig);
        setVapiConfig(prev => ({ ...prev, ...parsedConfig }));
      } catch (error) {
        console.error('Error loading Vapi config:', error);
      }
    }
  }, []);

  useEffect(() => {
    initializeVapi(vapiConfig.publicKey, useWebsiteContext);
    return () => stopCall();
  }, [vapiConfig.publicKey, useWebsiteContext]);

  // Save configurations
  useEffect(() => {
    const customFunctions = availableFunctions.filter(f => f.isCustom);
    localStorage.setItem('customFunctions', JSON.stringify(customFunctions));
  }, [availableFunctions]);

  useEffect(() => {
    // Save advanced Vapi config
    const configToSave = { ...vapiConfig };
    delete configToSave.publicKey; // Don't save sensitive data
    localStorage.setItem('vapiAdvancedConfig', JSON.stringify(configToSave));
  }, [vapiConfig]);

  // Voice Activity Monitoring
  useEffect(() => {
    if (voiceActivity) {
      setVoiceActivityMetrics(prev => ({
        ...prev,
        currentLevel: voiceActivity.level || 0,
        isSpeaking: voiceActivity.isSpeaking || false,
        speechDuration: voiceActivity.speechDuration || 0,
        silenceDuration: voiceActivity.silenceDuration || 0,
        interruptions: voiceActivity.interruptions || prev.interruptions,
        lastActivity: voiceActivity.timestamp || new Date()
      }));
    }
  }, [voiceActivity]);

  // Helper Functions
  const generateSystemPrompt = () => {
    const enabledFunctions = availableFunctions.filter(f => f.enabled);
    let prompt = vapiConfig.systemPrompt;

    if (useWebsiteContext && websiteContextData) {
      prompt = `You are a helpful AI assistant for this website. Keep responses concise and conversational for voice interaction.

WEBSITE CONTEXT: ${websiteContextData}

INSTRUCTIONS:
- Use website information to answer questions
- Keep responses under 30 seconds when spoken
- Be friendly and professional
- Adapt to interruptions gracefully
- Use natural conversation flow`;
    }

    if (enabledFunctions.length > 0) {
      prompt += `

AVAILABLE FUNCTIONS:
${enabledFunctions.map(f => `- ${f.name}: ${f.description}`).join('\n')}

When appropriate, offer to help customers by using these functions.`;
    }

    // Add interruption handling instructions
    if (vapiConfig.interruptionsEnabled) {
      prompt += `

INTERRUPTION HANDLING:
- If interrupted, acknowledge briefly and continue
- Adapt response based on interruption context
- Keep responses shorter after interruptions`;
    }

    return prompt + `\n\nUSER PROMPT: ${vapiConfig.systemPrompt}`;
  };

  const createAssistantConfig = () => {
    const enabledFunctions = availableFunctions.filter(f => f.enabled);
    const voiceConfig = { 
      provider: vapiConfig.voiceProvider, 
      voiceId: vapiConfig.voice 
    };

    // Apply voice settings based on provider
    if (vapiConfig.voiceProvider === 'elevenlabs') {
      Object.assign(voiceConfig, voiceSettings);
    } else {
      voiceConfig.speed = voiceSettings.speed;
    }

    // Complete Vapi Configuration
    const config = {
      // Core Model Configuration
      model: {
        provider: "openai",
        model: vapiConfig.model,
        temperature: vapiConfig.temperature,
        maxTokens: vapiConfig.maxTokens,
        messages: [{ role: "system", content: generateSystemPrompt() }]
      },
      
      // Voice Configuration
      voice: voiceConfig,
      
      // First Message
      firstMessage: useWebsiteContext && websiteContextData
        ? "Hello! I'm here to help with questions about our website and services. How can I assist you?"
        : "Hello! How can I help you today?",

      // Advanced Voice Activity Detection & Interruption Settings
      silenceTimeoutMs: vapiConfig.silenceTimeoutMs,
      responseDelayMs: vapiConfig.responseDelayMs,
      llmRequestDelayMs: vapiConfig.llmRequestDelayMs,
      interruptionsEnabled: vapiConfig.interruptionsEnabled,
      backchannelingEnabled: vapiConfig.backchannelingEnabled,
      backgroundDenoisingEnabled: vapiConfig.backgroundDenoisingEnabled,
      endCallOnSilence: vapiConfig.endCallOnSilence,
      maxCallDurationMs: vapiConfig.maxCallDurationMs,

      // Voice Activity Detection Configuration
      voiceActivityDetection: vapiConfig.voiceActivityDetection,

      // Recording & Analytics
      recordingEnabled: vapiConfig.recordingEnabled,
      analyticsEnabled: vapiConfig.analyticsEnabled,
      transcriptEnabled: vapiConfig.transcriptEnabled,
      summaryEnabled: vapiConfig.summaryEnabled,

      // Language Configuration
      language: vapiConfig.language,
      locale: vapiConfig.locale,

      // Server Configuration (if using custom server)
      ...(vapiConfig.serverUrl && {
        serverUrl: vapiConfig.serverUrl,
        serverUrlSecret: vapiConfig.serverUrlSecret
      }),

      // Advanced Features
      hipaaEnabled: vapiConfig.hipaaEnabled,
      clientMessages: vapiConfig.clientMessages,
      serverMessages: vapiConfig.serverMessages,
      transportConfigurations: vapiConfig.transportConfigurations
    };

    // Add Functions if enabled
    if (enabledFunctions.length > 0) {
      config.model.functions = enabledFunctions.map(f => ({
        name: f.name,
        description: f.description,
        parameters: f.parameters
      }));
    }

    return config;
  };

  const handleStartCall = async () => {
    if (!vapiConfig.publicKey) {
      toast({
        title: 'API Key Required',
        description: 'Please enter your Vapi public key',
        status: 'warning', duration: 5000, isClosable: true
      });
      return;
    }

    const config = vapiConfig.assistantId ? vapiConfig.assistantId : createAssistantConfig();
    await startCall(config);
  };

  // Function Testing
  const testFunction = async (functionName, testParams) => {
    const func = availableFunctions.find(f => f.name === functionName);
    if (!func) {
      return { success: false, message: 'Function not found' };
    }

    if (func.isCustom && func.implementation) {
      try {
        const implementationFunc = new Function('params', func.implementation);
        const result = await implementationFunc(testParams);
        return result;
      } catch (error) {
        return { success: false, message: `Implementation error: ${error.message}` };
      }
    }

    await new Promise(resolve => setTimeout(resolve, 1000));
    return {
      success: true,
      message: `Test successful for ${functionName}`,
      data: {
        functionName,
        testParams,
        timestamp: new Date().toISOString()
      }
    };
  };

  // Quick Actions
  const useBudgetConfig = () => {
    setVapiConfig(prev => ({
      ...prev, 
      model: 'gpt-3.5-turbo', 
      voiceProvider: 'openai',
      voice: 'alloy', 
      systemPrompt: 'Keep responses under 15 words to save costs.',
      maxTokens: 100,
      silenceTimeoutMs: 800,
      responseDelayMs: 200
    }));
    setVoiceSettings({ speed: 1.2, stability: 0.5, similarityBoost: 0.5, style: 0.0, useSpeakerBoost: false });
    toast({ title: 'Budget Mode Enabled', status: 'info', duration: 3000 });
  };

  const useConversationalConfig = () => {
    setVapiConfig(prev => ({
      ...prev,
      interruptionsEnabled: true,
      backchannelingEnabled: true,
      silenceTimeoutMs: 1200,
      responseDelayMs: 400,
      voiceActivityDetection: {
        ...prev.voiceActivityDetection,
        enabled: true,
        threshold: 0.3,
        speechTimeoutMs: 2500,
        endpointingTimeoutMs: 800
      }
    }));
    toast({ title: 'Conversational Mode Enabled', status: 'success', duration: 3000 });
  };

  const useFormalConfig = () => {
    setVapiConfig(prev => ({
      ...prev,
      interruptionsEnabled: false,
      backchannelingEnabled: false,
      silenceTimeoutMs: 2000,
      responseDelayMs: 600,
      voiceActivityDetection: {
        ...prev.voiceActivityDetection,
        enabled: true,
        threshold: 0.7,
        speechTimeoutMs: 4000,
        endpointingTimeoutMs: 1500
      }
    }));
    toast({ title: 'Formal Mode Enabled', status: 'info', duration: 3000 });
  };

  const resetVoiceSettings = () => {
    setVoiceSettings({ speed: 1.0, stability: 0.5, similarityBoost: 0.5, style: 0.0, useSpeakerBoost: true });
    toast({ title: 'Voice Settings Reset', status: 'info', duration: 2000 });
  };

  const reloadWebsiteContext = () => {
    const savedSettings = localStorage.getItem('settings');
    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings);
        if (parsedSettings.contextText) {
          setWebsiteContextData(parsedSettings.contextText);
          toast({
            title: 'Website Context Reloaded',
            description: `Loaded ${parsedSettings.contextText.length} characters`,
            status: 'success', duration: 3000
          });
        } else {
          toast({ title: 'No Website Context Found', status: 'warning', duration: 4000 });
        }
      } catch (error) {
        toast({ title: 'Error Loading Context', status: 'error', duration: 3000 });
      }
    } else {
      toast({ title: 'No Data Found', status: 'warning', duration: 4000 });
    }
  };

  const clearFunctionHistory = () => {
    toast({ title: 'Function History Cleared', status: 'info', duration: 2000 });
  };

  const toggleFunction = (functionName) => {
    setAvailableFunctions(prev =>
      prev.map(f => f.name === functionName ? { ...f, enabled: !f.enabled } : f)
    );
  };

  const enabledFunctions = availableFunctions.filter(f => f.enabled);

  return (
    <Box p={6} maxW="1400px" mx="auto">
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box textAlign="center">
          <Heading size="xl" color="blue.500" mb={2}>
            <Icon as={FaRobot} mr={3} />
            Vapi Voice AI Platform
          </Heading>
          <Text color="gray.600" fontSize="lg">
            Professional voice AI with real-time voice activity detection & interruption handling
          </Text>
        </Box>

        {/* Status Alerts */}
        {enabledFunctions.length > 0 && (
          <Alert status="success" borderRadius="md">
            <AlertIcon />
            <VStack align="start" spacing={1} flex="1">
              <Text fontWeight="bold">🔧 Function Calling Enabled</Text>
              <Text fontSize="sm">
                {enabledFunctions.length} business functions available: {enabledFunctions.map(f => f.name).join(', ')}
              </Text>
            </VStack>
          </Alert>
        )}

        {websiteContextData && (
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <VStack align="start" spacing={1} flex="1">
              <Text fontWeight="bold">📊 Website Context Loaded</Text>
              <Text fontSize="sm">{websiteContextData.length.toLocaleString()} characters available</Text>
            </VStack>
          </Alert>
        )}

        {vapiConfig.interruptionsEnabled && (
          <Alert status="success" borderRadius="md">
            <AlertIcon />
            <VStack align="start" spacing={1} flex="1">
              <Text fontWeight="bold">🎙️ Voice Activity Detection Active</Text>
              <Text fontSize="sm">
                Real-time interruption handling enabled • Threshold: {vapiConfig.voiceActivityDetection.threshold} • 
                Timeout: {vapiConfig.silenceTimeoutMs}ms
              </Text>
            </VStack>
          </Alert>
        )}

        <Tabs variant="enclosed" colorScheme="blue">
          <TabList>
            <Tab><Icon as={FaCog} mr={2} />Configuration</Tab>
            <Tab><Icon as={FaWaveSquare} mr={2} />Voice Activity</Tab>
            <Tab><Icon as={FaTools} mr={2} />Function Builder</Tab>
            <Tab><Icon as={FaMicrophone} mr={2} />Voice Interface</Tab>
          </TabList>

          <TabPanels>
            {/* Configuration Tab */}
            <TabPanel>
              <Flex gap={6} align="start">
                <VStack spacing={6} align="stretch" flex="2">
                  <ConfigurationPanel
                    vapiConfig={vapiConfig} setVapiConfig={setVapiConfig}
                    useWebsiteContext={useWebsiteContext} setUseWebsiteContext={setUseWebsiteContext}
                    websiteContextData={websiteContextData}
                    useBudgetConfig={useBudgetConfig} 
                    useConversationalConfig={useConversationalConfig}
                    useFormalConfig={useFormalConfig}
                    resetVoiceSettings={resetVoiceSettings}
                    clearFunctionHistory={clearFunctionHistory} 
                    reloadWebsiteContext={reloadWebsiteContext}
                  />

                  <FunctionCallingPanel
                    availableFunctions={availableFunctions}
                    toggleFunction={toggleFunction}
                  />

                  <AdvancedVoiceSettings
                    vapiConfig={vapiConfig}
                    voiceSettings={voiceSettings}
                    setVoiceSettings={setVoiceSettings}
                  />
                </VStack>

                <VStack spacing={6} align="stretch" flex="1" minW="300px">
                  <FunctionCallMonitor
                    functionCalls={functionCalls}
                    availableFunctions={availableFunctions}
                  />
                </VStack>
              </Flex>
            </TabPanel>

            {/* Voice Activity Tab */}
            <TabPanel>
              <VoiceActivitySettings
                vapiConfig={vapiConfig}
                setVapiConfig={setVapiConfig}
                voiceActivityMetrics={voiceActivityMetrics}
                isConnected={isConnected}
                callMetrics={callMetrics}
                callDuration={callDuration}
              />
            </TabPanel>

            {/* Function Builder Tab */}
            <TabPanel>
              <FunctionBuilder
                availableFunctions={availableFunctions}
                setAvailableFunctions={setAvailableFunctions}
                testFunction={testFunction}
              />
            </TabPanel>

            {/* Voice Interface Tab */}
            <TabPanel>
              <Flex gap={6} align="start">
                <VStack spacing={6} align="stretch" flex="2">
                  {/* Status Section */}
                  <Alert status={isConnected ? "success" : vapiConfig.publicKey ? "info" : "warning"} borderRadius="md">
                    <AlertIcon />
                    <VStack align="start" spacing={1} flex="1">
                      <Text fontWeight="bold">
                        {isConnected ? "🟢 Voice AI Active" : 
                         vapiConfig.publicKey ? "🟡 Ready to Start" : 
                         "🔴 Enter API Key"}
                      </Text>
                      <Text fontSize="sm">
                        Status: {isConnected ? 'Live conversation in progress' : 'Ready to connect'}
                        {callDuration && ` • Duration: ${Math.floor(callDuration / 60)}:${(callDuration % 60).toString().padStart(2, '0')}`}
                        {callCost && ` • Cost: $${callCost.toFixed(4)}`}
                      </Text>
                    </VStack>
                  </Alert>

                  {/* Voice Activity Visualization */}
                  {isConnected && (
                    <Card bg={cardBg}>
                      <CardBody>
                        <VStack spacing={4}>
                          <Text fontWeight="bold">Real-time Voice Activity</Text>
                          <Flex gap={6} justify="center" align="center" w="100%">
                            <VStack>
                              <Text fontSize="sm">Voice Level</Text>
                              <Box w="100px" h="20px" bg="gray.200" borderRadius="full" overflow="hidden">
                                <Box 
                                  w={`${voiceActivityMetrics.currentLevel * 100}%`} 
                                  h="100%" 
                                  bg={voiceActivityMetrics.isSpeaking ? "green.400" : "blue.400"}
                                  transition="width 0.1s ease"
                                />
                              </Box>
                            </VStack>
                            <VStack>
                              <Badge colorScheme={voiceActivityMetrics.isSpeaking ? "green" : "gray"}>
                                {voiceActivityMetrics.isSpeaking ? "Speaking" : "Silent"}
                              </Badge>
                              <Text fontSize="xs">
                                Interruptions: {voiceActivityMetrics.interruptions}
                              </Text>
                            </VStack>
                          </Flex>
                        </VStack>
                      </CardBody>
                    </Card>
                  )}

                  {/* Main Voice Interface */}
                  <Card bg={cardBg}>
                    <CardBody>
                      <VStack spacing={6}>
                        <Heading size="md">Voice Conversation</Heading>
                        
                        <Box 
                          w="150px" h="150px" borderRadius="full" 
                          bg={isConnected ? 'green.50' : isLoading ? 'blue.50' : 'gray.100'}
                          display="flex" alignItems="center" justifyContent="center"
                          border={`4px solid ${isConnected ? 'green.400' : isLoading ? 'blue.400' : 'gray.300'}`}
                          transition="all 0.3s ease"
                          position="relative"
                        >
                          {isLoading ? (
                            <Spinner size="xl" color="blue.500" />
                          ) : (
                            <Icon as={isConnected ? FaVolumeUp : FaPlay} boxSize={10} 
                                  color={isConnected ? 'green.500' : 'gray.500'} />
                          )}
                          
                          {/* Voice activity ring */}
                          {isConnected && voiceActivityMetrics.isSpeaking && (
                            <Box
                              position="absolute"
                              w="160px" h="160px"
                              borderRadius="full"
                              border="2px solid"
                              borderColor="green.300"
                              animation="pulse 1s infinite"
                            />
                          )}
                        </Box>

                        <VStack spacing={4} w="100%">
                          <Badge colorScheme={isConnected ? "green" : isLoading ? "blue" : "gray"}
                                 fontSize="md" p={2} borderRadius="md">
                            {isConnected ? '🎤 Listening...' : 
                             isLoading ? '🔄 Connecting...' : 
                             '⏸️ Ready to Start'}
                          </Badge>
                          
                          {transcript && (
                            <Box p={4} bg="blue.50" borderRadius="md" w="100%">
                              <Text fontWeight="bold" fontSize="sm" mb={1} color="blue.700">You said:</Text>
                              <Text>{transcript}</Text>
                            </Box>
                          )}
                          
                          {aiResponse && (
                            <Box p={4} bg="green.50" borderRadius="md" w="100%">
                              <Text fontWeight="bold" fontSize="sm" mb={1} color="green.700">AI Response:</Text>
                              <Text>{aiResponse}</Text>
                            </Box>
                          )}

                          {conversationHistory.length > 0 && (
                            <Box w="100%" maxH="200px" overflowY="auto">
                              <Text fontWeight="bold" fontSize="sm" mb={2}>Conversation History:</Text>
                              {conversationHistory.slice(-5).map((item, index) => (
                                <Box key={index} p={2} mb={2} 
                                     bg={item.type === 'user' ? 'blue.50' : 'green.50'} 
                                     borderRadius="md">
                                  <Text fontSize="xs" color="gray.600">
                                    {item.type === 'user' ? '👤 You' : '🤖 AI'} - {item.timestamp.toLocaleTimeString()}
                                  </Text>
                                  <Text fontSize="sm">{item.content}</Text>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </VStack>

                        <VStack spacing={4}>
                          <Button
                            colorScheme={isConnected ? "red" : "green"} size="lg"
                            onClick={isConnected ? stopCall : handleStartCall}
                            leftIcon={<Icon as={isConnected ? FaStop : FaMicrophone} />}
                            isLoading={isLoading} isDisabled={!vapiConfig.publicKey}
                          >
                            {isConnected ? 'End Conversation' : 'Start Voice AI'}
                          </Button>
                          
                          {!vapiConfig.publicKey && (
                            <Text fontSize="sm" color="gray.500" textAlign="center">
                              Enter your Vapi public key in Configuration tab to get started
                            </Text>
                          )}
                        </VStack>
                      </VStack>
                    </CardBody>
                  </Card>
                </VStack>

                {/* Right Column - Function Monitor */}
                <VStack spacing={6} align="stretch" flex="1" minW="300px">
                  <FunctionCallMonitor
                    functionCalls={functionCalls}
                    availableFunctions={availableFunctions}
                  />
                </VStack>
              </Flex>
            </TabPanel>
          </TabPanels>
        </Tabs>

        {/* Error Display */}
        {lastError && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Box>
              <Text fontWeight="bold">Error:</Text>
              <Text fontSize="sm">{lastError}</Text>
            </Box>
          </Alert>
        )}
      </VStack>
    </Box>
  );
};

export default VoiceAIDemo;